/**
 * Shared skeleton card base styles and helpers
 * Eliminates duplication across skeleton components
 */

import type { ViewStyle } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

export interface SkeletonCardShadow {
  shadowColor: string;
  shadowOffset: { width: number; height: number };
  shadowOpacity: number;
  shadowRadius: number;
  elevation: number;
}

export const skeletonCardShadows = {
  small: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 3,
  },
  medium: {
    shadowColor: 'rgba(15, 23, 42, 0.08)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  large: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 6,
  },
} as const;

export interface CreateSkeletonCardBaseOptions {
  borderRadius?: number;
  padding?: number;
  paddingHorizontal?: number;
  paddingVertical?: number;
  shadow?: keyof typeof skeletonCardShadows;
  borderWidth?: number;
  borderColor?: string;
  marginVertical?: number;
  marginBottom?: number;
}

/**
 * Creates base card style for skeletons with theme-aware background
 */
export const createSkeletonCardBase = (
  theme: Theme,
  options?: CreateSkeletonCardBaseOptions
): ViewStyle => {
  const {
    borderRadius = 12,
    padding,
    paddingHorizontal,
    paddingVertical,
    shadow = 'medium',
    borderWidth = 0,
    borderColor,
    marginVertical,
    marginBottom,
  } = options || {};

  // Use padding if provided, otherwise use separate horizontal/vertical
  const paddingStyle = padding !== undefined
    ? { padding }
    : {
        ...(paddingHorizontal !== undefined && { paddingHorizontal }),
        ...(paddingVertical !== undefined && { paddingVertical }),
      };

  return {
    backgroundColor: theme.background.card,
    borderRadius,
    ...paddingStyle,
    borderWidth,
    borderColor: borderColor || theme.gray[200],
    ...(marginVertical !== undefined && { marginVertical }),
    ...(marginBottom !== undefined && { marginBottom }),
    ...skeletonCardShadows[shadow],
  };
};

